/**
 * DomainPanelGrid — 2-column accordion grid of 7 domain panels.
 *
 * Behavior:
 *   - Single-open: clicking a panel opens it and closes any other.
 *   - Toggle All: expands/collapses all panels at once.
 *   - scrollIntoView: opened panel scrolls to center of viewport.
 *   - Each panel header shows: accent bar + domain name + status chips + 3 KPI minis + chevron.
 *   - Expanded body shows: 3 metric cells + signal items + "View [Domain] →" link.
 */
import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronsUpDown, Info } from 'lucide-react';
import { cn } from '../ui/lib/utils';
import { SectionHeading } from '../ui/SectionHeading';
import { StatusBadge } from '../ui/StatusBadge';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';
import { ICON_PX } from '../ui/lib/iconSizes';

// ─── Domain data ─────────────────────────────────────────────────────────────
const DOMAINS = [
  {
    key: 'staffing',
    name: 'Staffing',
    accent: 'var(--yellow)',
    route: '/manager/staffing',
    chips: [{ text: '2 Gaps', variant: 'critical' }, { text: 'OT Risk', variant: 'watch' }],
    kpis: [
      { label: 'Coverage', value: '11/13', alert: true },
      { label: 'Float', value: 'Pending', alert: true },
      { label: 'OT Hrs', value: '2.5h' },
    ],
    metrics: [
      { label: 'AM Shift', value: '7/7', sub: 'All filled', status: 'ok' },
      { label: 'PM Shift', value: '5/7', sub: '2 RN gaps', status: 'critical' },
      { label: 'Night Shift', value: '6/7', sub: '1 pending', status: 'watch' },
    ],
    signals: [
      { severity: 'critical', text: 'PM shift — 2 RN roles uncovered. Float pool request submitted 6:45 AM.', action: 'View Queue →' },
      { severity: 'watch', text: 'S. Chen BLS expires Mar 31. Renewal not yet scheduled.', action: 'Assign Task →' },
    ],
  },
  {
    key: 'access',
    name: 'Scheduling & Access',
    accent: 'var(--aqua)',
    route: '/manager/access',
    chips: [{ text: 'Below Target', variant: 'critical' }],
    kpis: [
      { label: 'Same-Day', value: '71%', alert: true },
      { label: 'No-Show', value: '14%', alert: true },
      { label: 'Open Slots', value: '6' },
    ],
    metrics: [
      { label: 'Same-Day Rate', value: '71%', sub: 'Target: 85%', status: 'critical' },
      { label: 'No-Show Rate', value: '14%', sub: '↑ 3% this week', status: 'watch' },
      { label: 'Avg Wait', value: '22 min', sub: 'Target: 15 min' },
    ],
    signals: [
      { severity: 'critical', text: 'Same-day access 14pts below target. 6 slots available, 4 hold-backs to release.', action: 'Release Slots →' },
      { severity: 'watch', text: '3 no-shows before 8 AM. Waitlist has 5 patients.', action: 'Activate Waitlist →' },
    ],
  },
  {
    key: 'health',
    name: 'Population Health',
    accent: 'var(--teal-dark)',
    route: '/manager/population-health',
    chips: [{ text: '3 High-Priority', variant: 'watch' }],
    kpis: [
      { label: 'Outreach', value: '62%', alert: true },
      { label: 'Backlog', value: '17' },
      { label: 'Care Gaps', value: '↓4', status: 'ok' },
    ],
    metrics: [
      { label: 'Outreach Rate', value: '62%', sub: '28/45 patients', status: 'watch' },
      { label: 'High-Risk Pending', value: '3', sub: '7-day window', status: 'critical' },
      { label: 'Care Gaps Closed', value: '↓4', sub: 'This week', status: 'ok' },
    ],
    signals: [
      { severity: 'critical', text: '3 transitions follow-ups in 7-day window. Care coordinator absent — tasks orphaned.', action: 'Reassign →' },
      { severity: 'watch', text: 'Diabetic A1C outreach 62% complete. 15 patients unreached. Target: 80%.', action: 'View Patients →' },
    ],
  },
  {
    key: 'finance',
    name: 'Financial Tracking',
    accent: 'var(--green)',
    route: '/manager/finance',
    chips: [{ text: 'On Budget', variant: 'on-track' }],
    kpis: [
      { label: 'Variance', value: '−1.2%' },
      { label: 'OT Cost', value: '$840', alert: true },
      { label: 'Rev/Appt', value: '$148' },
    ],
    metrics: [
      { label: 'Budget Variance', value: '−1.2%', sub: 'Under budget', status: 'ok' },
      { label: 'OT This Week', value: '$840', sub: '↑ $200 vs last wk', status: 'watch' },
      { label: 'Revenue/Appt', value: '$148', sub: 'Blended rate' },
    ],
    signals: [
      { severity: 'watch', text: 'OT cost up $200 vs last week. 2.5 unplanned OT hours from staffing gap.', action: 'View Details →' },
    ],
  },
  {
    key: 'consumer',
    name: 'Consumer Insights',
    accent: 'var(--orange-warn)',
    route: '/manager/consumer-insights',
    chips: [{ text: '4.3 / 5.0', variant: 'on-track' }],
    kpis: [
      { label: 'Satisfaction', value: '4.3' },
      { label: 'Neg Reviews', value: '3', alert: true },
      { label: 'Phone Abdn', value: '8%' },
    ],
    metrics: [
      { label: 'Patient Sat', value: '4.3/5', sub: '↑ 0.2 vs Q3', status: 'ok' },
      { label: 'Neg Reviews', value: '3', sub: 'This month', status: 'watch' },
      { label: 'Phone Abandon', value: '8%', sub: 'Target: <7%' },
    ],
    signals: [
      { severity: 'watch', text: '3 negative reviews mentioning wait times. Likely tied to scheduling gap.', action: 'View Reviews →' },
    ],
  },
  {
    key: 'practice',
    name: 'Practice Management',
    accent: 'var(--royal)',
    route: '/manager/practice-management',
    chips: [{ text: '3 Overdue', variant: 'critical' }],
    kpis: [
      { label: 'Done Today', value: '67%' },
      { label: 'Overdue', value: '3', alert: true },
      { label: 'Skip Rate', value: '2%' },
    ],
    metrics: [
      { label: 'Completion', value: '67%', sub: '12/18 tasks', status: 'watch' },
      { label: 'Overdue Tasks', value: '3', sub: '2 AM + 1 yesterday', status: 'critical' },
      { label: 'Checklists', value: '2 due', sub: 'EOC + Rounding', status: 'watch' },
    ],
    signals: [
      { severity: 'critical', text: 'EOC checklist overdue. Assigned staff called out — reassign needed.', action: 'Reassign →' },
      { severity: 'watch', text: 'Intentional rounding checklist due by 10 AM.', action: 'View Checklist →' },
    ],
  },
];

const SIGNAL_DOT = {
  critical: 'bg-red-critical',
  watch: 'bg-yellow-nn',
  info: 'bg-aqua',
};

const METRIC_COLOR = {
  critical: 'text-red-critical',
  watch: 'text-yellow-nn',
  ok: 'text-green-nn',
};

// ─── Panel component ─────────────────────────────────────────────────────────
function DomainPanel({ domain, isOpen, onToggle }) {
  const navigate = useNavigate();
  const panelRef = useRef(null);
  const d = domain;

  return (
    <div
      ref={panelRef}
      className={cn(
        'rounded-card bg-nn-white shadow-card border border-nn-border overflow-hidden',
        'transition-shadow hover:shadow-md',
        'animate-fade-up'
      )}
    >
      {/* Header — always visible */}
      <button
        type="button"
        onClick={() => {
          onToggle();
          if (!isOpen) {
            setTimeout(() => {
              panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 350);
          }
        }}
        className={cn(
          'w-full flex items-center cursor-pointer text-left',
          isOpen && 'border-b border-nn-border'
        )}
      >
        {/* Accent bar */}
        <div className="w-[3px] self-stretch shrink-0" style={{ background: d.accent }} />

        {/* Inner content */}
        <div className="flex items-center gap-2 flex-1 min-w-0 px-3 py-2">
          {/* Domain name */}
          <span className="text-[13px] font-semibold text-text-dark whitespace-nowrap shrink-0">
            {d.name}
          </span>

          {/* Status chips */}
          <div className="flex items-center gap-1 shrink-0">
            {d.chips.map((c) => (
              <StatusBadge key={c.text} variant={c.variant} size="sm">{c.text}</StatusBadge>
            ))}
          </div>

          {/* KPI mini strip — pushed right */}
          <div className="flex items-center gap-3 ml-auto shrink-0 pr-1">
            {d.kpis.map((k) => (
              <div key={k.label} className="text-right">
                <div className="text-[9px] text-text-muted leading-tight">{k.label}</div>
                <div className={cn(
                  'text-[12px] font-bold leading-tight',
                  k.alert ? 'text-red-critical' : 'text-text-dark'
                )}>
                  {k.value}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Chevron */}
        <div className={cn(
          'shrink-0 px-3 py-2 text-text-muted transition-transform',
          isOpen && 'rotate-180'
        )}>
          <ChevronDown size={14} />
        </div>
      </button>

      {/* Body — expanded content */}
      <div
        className="overflow-hidden transition-[max-height] duration-300 ease-in-out"
        style={{ maxHeight: isOpen ? 360 : 0 }}
      >
        <div className="p-3">
          {/* Metric cells */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            {d.metrics.map((m) => (
              <div key={m.label} className="bg-surface rounded p-2">
                <div className="text-[10px] text-text-muted leading-tight">{m.label}</div>
                <div className={cn(
                  'text-[16px] font-bold leading-tight mt-0.5',
                  METRIC_COLOR[m.status] || 'text-text-dark'
                )}>
                  {m.value}
                </div>
                {m.sub && <div className="text-[9px] text-text-muted mt-0.5">{m.sub}</div>}
              </div>
            ))}
          </div>

          {/* Signal items */}
          <div className="flex flex-col gap-1.5 mb-3">
            {d.signals.map((s, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className={cn('w-1.5 h-1.5 rounded-full mt-1.5 shrink-0', SIGNAL_DOT[s.severity])} />
                <span className="text-[11px] text-text-dark leading-snug flex-1">{s.text}</span>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between">
            {d.routeDisabled ? (
              <span className="text-[11px] font-semibold text-text-muted">
                View {d.name} → (coming soon)
              </span>
            ) : (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); navigate(d.route); }}
                className="text-[11px] font-semibold text-aqua hover:underline"
              >
                View {d.name} →
              </button>
            )}
            <span className="text-[9px] text-text-muted">Updated 7:01 AM</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main grid ───────────────────────────────────────────────────────────────
// Row pairs — panels that share a grid row open together
const ROW_PAIRS = {
  staffing: 'access',    access: 'staffing',
  health: 'finance',     finance: 'health',
  consumer: 'practice',  practice: 'consumer',
};

export default function DomainPanelGrid() {
  const [openKeys, setOpenKeys] = useState(new Set());
  const [expandAll, setExpandAll] = useState(false);
  const sectionRef = useRef(null);

  const togglePanel = useCallback((key) => {
    if (expandAll) {
      setExpandAll(false);
      const pair = ROW_PAIRS[key];
      setOpenKeys(new Set(pair ? [key, pair] : [key]));
      return;
    }
    setOpenKeys((prev) => {
      if (prev.has(key)) {
        // Closing — clear the row pair
        return new Set();
      }
      // Opening — open this panel + its row-mate
      const pair = ROW_PAIRS[key];
      return new Set(pair ? [key, pair] : [key]);
    });
  }, [expandAll]);

  const toggleAll = () => {
    if (expandAll) {
      // Collapse all — scroll section heading back into view
      setExpandAll(false);
      setOpenKeys(new Set());
      setTimeout(() => {
        sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 350);
    } else {
      // Expand all — scroll section heading to top so panels are visible
      setExpandAll(true);
      setOpenKeys(new Set());
      setTimeout(() => {
        sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 350);
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div ref={sectionRef} className="mt-2">
        <SectionHeading
          title="Domain Dashboards"
          size="sm"
          className="mb-1.5"
          aiContext="practice-pulse.domain-health"
          action={
            <div className="flex items-center gap-1.5">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center text-text-muted hover:text-text-dark-secondary transition-colors"
                  >
                    <Info size={ICON_PX.md} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" maxWidth={220}>
                  At-a-glance status for each operational domain. Click a panel to expand its details, or Toggle All to see everything.
                </TooltipContent>
              </Tooltip>
              <button
                type="button"
                onClick={toggleAll}
                className={cn(
                  'flex items-center gap-1 text-[11px] font-semibold',
                  'text-text-dark-secondary bg-surface-warm border border-nn-border',
                  'rounded px-2 py-1 hover:bg-surface transition-colors'
                )}
              >
                <ChevronsUpDown size={12} />
                {expandAll ? 'Collapse All' : 'Expand All'}
              </button>
            </div>
          }
        />

        <div className="grid grid-cols-2 gap-2.5">
          {DOMAINS.map((d) => (
            <DomainPanel
              key={d.key}
              domain={d}
              isOpen={expandAll || openKeys.has(d.key)}
              onToggle={() => togglePanel(d.key)}
            />
          ))}
        </div>
      </div>
    </TooltipProvider>
  );
}
